import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Radio, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { CONTRACT_TERMS, cancelContract, renewContract, type Contract } from './contractApi'

interface DecisionProps {
  contract: Contract | null
  onClose: () => void
}

function useContractChanged() {
  const queryClient = useQueryClient()
  return (contract: Contract) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.contracts(contract.subscriberId) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all })
  }
}

export function ContractRenewModal({ contract, onClose }: DecisionProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ termYears: number }>()
  const changed = useContractChanged()

  useEffect(() => {
    if (contract) form.setFieldsValue({ termYears: contract.termYears })
  }, [contract, form])

  const renew = useMutation({
    mutationFn: (termYears: number) => renewContract(contract?.id ?? '', termYears),
    onSuccess: (saved) => {
      changed(saved)
      onClose()
    },
  })

  const close = () => {
    renew.reset()
    onClose()
  }

  return (
    <Modal
      open={contract !== null}
      title={t('contract.renewTitle', { number: contract?.number ?? '' })}
      okText={t('contract.renew')}
      cancelText={t('common.cancel')}
      confirmLoading={renew.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => renew.mutate(values.termYears))}
      destroyOnHidden
    >
      {renew.isError ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message={translateError(renew.error, t)} /> : null}
      <Typography.Paragraph type="secondary">
        {t('contract.renewIntro', { endsOn: contract?.endsOn ?? '' })}
      </Typography.Paragraph>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="termYears" label={t('contract.fields.termYears')}>
          <Radio.Group
            optionType="button"
            options={CONTRACT_TERMS.map((value) => ({ value, label: t('contract.years', { count: value }) }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export function ContractCancelModal({ contract, onClose }: DecisionProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ reason: string }>()
  const changed = useContractChanged()

  const cancel = useMutation({
    mutationFn: (reason: string) => cancelContract(contract?.id ?? '', reason),
    onSuccess: (saved) => {
      changed(saved)
      onClose()
    },
  })

  const close = () => {
    cancel.reset()
    onClose()
  }

  return (
    <Modal
      open={contract !== null}
      title={t('contract.cancelTitle', { number: contract?.number ?? '' })}
      okText={t('contract.cancel')}
      okButtonProps={{ danger: true }}
      cancelText={t('common.cancel')}
      confirmLoading={cancel.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => cancel.mutate(values.reason.trim()))}
      destroyOnHidden
    >
      {cancel.isError ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message={translateError(cancel.error, t)} /> : null}
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="reason"
          label={t('contract.cancelReason')}
          rules={[{ required: true, whitespace: true, message: t('contract.cancelReasonRequired') }]}
        >
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
